import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";

const PositionFilterDropdown = ({
  positions = [],
  selectedId,
  onSelect,
  placeholder = "Select position",
  className = "",
  buttonClassName = "",
  listClassName = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  const selectedLabel = useMemo(() => {
    const match = positions.find((p) => p.id === selectedId);
    return match ? match.label : placeholder;
  }, [positions, selectedId, placeholder]);

  const filteredPositions = useMemo(() => {
    if (!query.trim()) return positions;
    const q = query.trim().toLowerCase();
    return positions.filter((p) => {
      const label = (p.label || "").toLowerCase();
      const full = (p.fullLabel || "").toLowerCase();
      return label.includes(q) || full.includes(q);
    });
  }, [positions, query]);

  const handleDocumentClick = useCallback((event) => {
    if (!containerRef.current) return;
    if (!containerRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleDocumentClick);
    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, [handleDocumentClick]);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setHighlightedIndex(-1);
      return;
    }
    setTimeout(() => {
      if (listRef.current) listRef.current.focus();
    }, 0);
  }, [isOpen]);

  const closeDropdown = () => setIsOpen(false);
  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const handleKeyDownButton = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleDropdown();
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setIsOpen(true);
      setHighlightedIndex(0);
    }
  };

  const handleKeyDownList = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeDropdown();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((prev) => {
        const next = Math.min(prev + 1, filteredPositions.length - 1);
        return next;
      });
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const item = filteredPositions[highlightedIndex];
      if (item) {
        onSelect?.(item.id);
        closeDropdown();
      }
    }
  };

  const handleClickItem = (positionId) => {
    onSelect?.(positionId);
    closeDropdown();
  };

  return (
    <div
      ref={containerRef}
      className={`relative inline-block ${className}`}
    >
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Position filter dropdown"
        onClick={toggleDropdown}
        onKeyDown={handleKeyDownButton}
        className={`w-full min-w-[220px] max-w-full text-left bg-white border border-[#E5E6E8] rounded-lg px-4 py-2 flex items-center justify-between gap-3 hover:bg-gray-50 ${buttonClassName}`}
      >
        <span 
          key={`position-${selectedId}`}
          className="truncate text-sm text-[#1E1E1E]"
          data-no-translate="true"
        >
          {selectedLabel}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : "rotate-0"}`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="2"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div
          className={`absolute z-20 mt-2 w-[min(90vw,22rem)] max-h-96 overflow-hidden bg-white border border-[#E5E6E8] rounded-lg shadow-lg ${listClassName}`}
        >
          <div className="p-2 border-b border-[#E5E6E8]">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search positions..."
              className="w-full px-3 py-2 text-sm border border-[#E5E6E8] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Search positions"
            />
          </div>
          <ul
            ref={listRef}
            role="listbox"
            tabIndex={0}
            onKeyDown={handleKeyDownList}
            className="max-h-72 overflow-auto py-1"
            aria-activedescendant={
              highlightedIndex >= 0 && filteredPositions[highlightedIndex]
                ? `position-option-${filteredPositions[highlightedIndex].id}`
                : undefined
            }
          >
            {filteredPositions.map((pos, index) => {
              const isSelected = pos.id === selectedId;
              const isHighlighted = index === highlightedIndex;
              const base = "flex items-center justify-between w-full px-3 py-2 text-sm cursor-pointer";
              const state = isHighlighted
                ? "bg-gray-100"
                : isSelected
                ? "bg-blue-50"
                : "";
              return (
                <li
                  id={`position-option-${pos.id}`}
                  key={pos.id}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleClickItem(pos.id)}
                  className={`${base} ${state}`}
                >
                  <div className="flex flex-col">
                    <span className="text-[#1E1E1E]" data-no-translate="true">{pos.label}</span>
                    {pos.fullLabel && (
                      <span className="text-[11px] text-[#6B7280]" data-no-translate="true">{pos.fullLabel}</span>
                    )}
                  </div>
                  {isSelected && (
                    <svg
                      className="w-4 h-4 text-blue-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </li>
              );
            })}
            {filteredPositions.length === 0 && (
              <li className="px-3 py-4 text-sm text-[#6B7280]">No results</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PositionFilterDropdown;


